/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { PreviousPeriod } from "~/components/DateRangeSelector/types";
import { dateFormat } from "~/constants";
import { formatShortDate } from "~/tools";

type DateRange = {
  from: string;
  to: string;
};

// This is a workaround for a problem with date input from keyboard, we cannot dynamically parse incomplete input date because it will be constantly reset to the full year
// E.g. User starts typing "12/12/1", the incomplete 0001-12-12 date will be parsed into `1901-12-12` ISO format, making it impossible to enter desired year
export function useDateValues(range?: DateRange) {
  const [startDate, setStartDate] = useState(formatShortDate(range?.from));
  const [endDate, setEndDate] = useState(formatShortDate(range?.to));

  const [previousPeriodSelected, setPreviousPeriodSelected] = useState<
    PreviousPeriod | undefined
  >(undefined);

  const handlePreviousPeriodClick = (value: PreviousPeriod) => {
    setPreviousPeriodSelected(value);
    const dates = getPreviousPeriodStartEndDates(value);

    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
  };

  useEffect(() => {
    setStartDate(formatShortDate(range?.from));
  }, [range?.from]);

  useEffect(() => {
    setEndDate(formatShortDate(range?.to));
  }, [range?.to]);

  return {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    previousPeriodSelected,
    setPreviousPeriodSelected,
    handlePrevPeriodClick: handlePreviousPeriodClick,
  };
}

function getPreviousPeriodStartEndDates(value: PreviousPeriod) {
  const today = dayjs().format(dateFormat.shortDate);
  switch (value) {
    case PreviousPeriod.PREV_3_MONTHS:
      return {
        startDate: getAdjustedDate(3, "month", false),
        endDate: today,
      };
    case PreviousPeriod.PREV_6_MONTHS:
      return {
        startDate: getAdjustedDate(6, "month", false),
        endDate: today,
      };
    case PreviousPeriod.PREV_12_MONTHS:
      return {
        startDate: getAdjustedDate(12, "month", false),
        endDate: today,
      };
    case PreviousPeriod.PREV_YEAR:
      return {
        startDate: getAdjustedDate(1, "year", false),
        endDate: getAdjustedDate(1, "year", true),
      };
    case PreviousPeriod.PREV_2_YEAR:
      return {
        startDate: getAdjustedDate(2, "year", false),
        endDate: getAdjustedDate(2, "year", true),
      };
    default:
      return {
        startDate: getAdjustedDate(0, "year", false),
        endDate: today,
      };
  }
}

function getAdjustedDate(
  adjustBy: number,
  period: "month" | "year",
  isEndDate: boolean
) {
  const date = dayjs().subtract(adjustBy, period);
  return (isEndDate ? date.endOf(period) : date.startOf(period)).format(
    dateFormat.shortDate
  );
}
